import { Button, Modal, addToast } from '@octano/global-ui';
import { useCallback, useState } from 'react';
import { Col, FormGroup, Row } from 'reactstrap';

import {
  AxiosResult,
  AxiosResultDefaultError,
} from '../../../../web/src/api/request';
import ImageCrop, { ImageCropTexts } from './ImageCrop';
import ImageSelect, { ImageSelectTexts } from './ImageSelect';

export interface ImageCropAndUploadModalProps {
  open?: boolean;
  currentPhoto?: string | null;
  texts: ImageCropAndUploadModalTexts;
  submitFuction: (
    photo: File | undefined,
  ) => Promise<AxiosResult<unknown, AxiosResultDefaultError>>;
  onClose: () => void;
  onSuccess: (src: string | null) => void;
  onError: (error: string) => void;
}

export interface FileAndSrc {
  file: File;
  src: string;
}

export interface ImageCropAndUploadModalTexts {
  select: ImageSelectTexts & { cancel: string; save: string };
  crop: ImageCropTexts;
  fileAccept: string;
  fileReject: string;
  fileRejectedSize: string;
}

const ImageCropAndUploadModal = (props: ImageCropAndUploadModalProps) => {
  const {
    open = false,
    texts,
    currentPhoto,
    submitFuction,
    onClose,
    onSuccess,
    onError,
  } = props;

  const [loading, setLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<FileAndSrc | null>(null);

  const submit = useCallback(async () => {
    setLoading(true);
    let result = await submitFuction(selectedPhoto?.file);
    if (result?.error) {
      onError(result.error.code);
    } else {
      onSuccess(selectedPhoto?.src ?? null);
    }

    setLoading(false);
  }, [selectedPhoto, onSuccess, onError, submitFuction]);

  const [step, setStep] = useState<'initial' | 'select' | 'crop'>('initial');
  const onSelect = useCallback((selection: FileAndSrc | null) => {
    if (selection) {
      setSelectedPhoto(selection);
      setStep('crop');
    } else {
      setSelectedPhoto(null);
      setStep('select');
    }
  }, []);

  const onSelectError = useCallback(() => {
    addToast({
      icon: 'error',
      color: 'danger',
      text: texts.fileRejectedSize,
    });
  }, [texts]);

  return (
    <Modal
      isOpen={open}
      toggle={onClose}
      unmountOnClose
      onOpened={() => {
        setStep('initial');
        setSelectedPhoto(null);
      }}
    >
      {step === 'initial' && (
        <>
          <ImageSelect
            currentPhoto={currentPhoto}
            texts={texts.select}
            onSelect={onSelect}
            onError={onSelectError}
          />
          <Row>
            <Col xs="12" lg="6" className="order-2 order-lg-1">
              <FormGroup>
                <Button
                  outlined
                  fullwidth
                  color="primary"
                  text={texts.select.cancel}
                  onClick={onClose}
                />
              </FormGroup>
            </Col>
            <Col xs="12" lg="6" className="order-1 order-lg-2">
              <FormGroup>
                <Button
                  fullwidth
                  color="primary"
                  text={texts.select.save}
                  disabled
                />
              </FormGroup>
            </Col>
          </Row>
        </>
      )}
      {step === 'crop' && (
        <ImageCrop
          texts={texts.crop}
          upImg={selectedPhoto?.src}
          onCompleted={(selection) => {
            if (!selection) {
              setStep('initial');
              setSelectedPhoto(null);
            } else {
              setStep('select');
              setSelectedPhoto(selection);
            }
          }}
        />
      )}
      {step === 'select' && (
        <>
          <ImageSelect
            currentPhoto={selectedPhoto?.src}
            texts={texts.select}
            onSelect={onSelect}
            onError={onSelectError}
          />
          <Row>
            <Col xs="12" lg="6" className="order-2 order-lg-1">
              <FormGroup>
                <Button
                  outlined
                  fullwidth
                  color="primary"
                  text={texts.select.cancel}
                  onClick={onClose}
                />
              </FormGroup>
            </Col>
            <Col xs="12" lg="6" className="order-1 order-lg-2">
              <FormGroup>
                <Button
                  fullwidth
                  color="primary"
                  text={texts.select.save}
                  onClick={submit}
                  loading={loading}
                />
              </FormGroup>
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );
};

export default ImageCropAndUploadModal;
